import { useCallback } from 'react';
import { Flex } from '../../../ui/layout/Flex';
import { Image } from '../../../ui/media/Image';
import { Typography } from '../../../ui/typography/Typography';
import { buildPublicUrl } from '../../../utils/buildUrl';
import { useIsMobile } from '../../../utils/getIsMobile';
import { useTranslation } from '../../../utils/useTranslation';
import { DialogRoute, useOpenDialog } from '../../ModalRoute';
import { DIALOGS } from '../../constants';
import { PRODUCTS_URL, SHOPS_URL } from './constants';
import { AdvertisingCatalogInfoCard } from './parts/AdvertisingCatalogInfoCard/AdvertisingCatalogInfoCard';
import { AdvertisingCatalogInfoDialog } from './parts/AdvertisingCatalogInfoDialog/AdvertisingCatalogInfoDialog';

export const AdvertisingCatalogStartPage: React.FC<{}> = () => {
    const t = useTranslation();

    const isMobile = useIsMobile();

    const openDialog = useOpenDialog();

    const onProductsInfoClick = useCallback(() => {
        openDialog(DIALOGS.CPA_ADVERTISING_PRODUCTS_INFO);
    }, [openDialog]);
    const onShopsInfoClick = useCallback(() => {
        openDialog(DIALOGS.CPA_ADVERTISING_SHOPS_INFO);
    }, [openDialog]);

    return (
        <>
            <Flex width="100%" height="100%" alignItems="center" justifyContent="center">
                <Typography
                    variant={isMobile ? 'title3' : 'title1'}
                    weight="heavy"
                    textAlign="center"
                    py={2}
                >
                    {t('Выберите, что будете рекламировать')}
                </Typography>
                <Flex
                    direction={{ xs: 'column', sm: 'row' }}
                    alignItems="center"
                    spacing={{ xs: 2, sm: 3 }}
                    mt={{ xs: 1, sm: 4 }}
                    width={{ xs: '100%', sm: 'auto' }}
                    overflowY="auto"
                >
                    <AdvertisingCatalogInfoCard
                        title={t('Товары из каталога')}
                        description={t('Найдите товар, а мы подберем подходящие магазины')}
                        url={PRODUCTS_URL}
                        onClick={onProductsInfoClick}
                    >
                        {isMobile ? (
                            <Image
                                width="100px"
                                height="75px"
                                src={buildPublicUrl('/images/productCardCompact.png')}
                            />
                        ) : (
                            <Image
                                width="300px"
                                height="220px"
                                src={buildPublicUrl('/images/productCard.png')}
                            />
                        )}
                    </AdvertisingCatalogInfoCard>
                    <AdvertisingCatalogInfoCard
                        title={t('Товары с сайта магазина')}
                        description={t('Найдите магазин и выберите товар из него')}
                        url={SHOPS_URL}
                        onClick={onShopsInfoClick}
                    >
                        {isMobile ? (
                            <Flex
                                borderRadius={3}
                                width="90px"
                                height="90px"
                                overflow="hidden"
                                flexShrink={0}
                            >
                                <Image
                                    width="90px"
                                    height="90px"
                                    src={buildPublicUrl('/images/shopCardCompact.png')}
                                />
                            </Flex>
                        ) : (
                            <Image
                                width="300px"
                                height="220px"
                                objectFit="contain"
                                src={buildPublicUrl('/images/shopCard.png')}
                            />
                        )}
                    </AdvertisingCatalogInfoCard>
                </Flex>
            </Flex>
            <DialogRoute
                name={DIALOGS.CPA_ADVERTISING_PRODUCTS_INFO}
                element={
                    <AdvertisingCatalogInfoDialog title={t('Товары из каталога')}>
                        <Typography variant="body">
                            {t('Выберите товар, а мы подберем подходящий магазин.')}
                        </Typography>
                        <Typography variant="body" pt={1}>
                            {t(
                                'Здесь собраны товары, которые продвигают бренды. Все товары из каталога участвуют в программе монетизации. Выберите товар для продвижения и скопируйте ссылку на него. Если товар продвигается в нескольких магазинах, то выберите тот, на который будете вести свою аудиторию. При создании поста добавьте ссылку на товар в поле «Товары в посте» и убедитесь, что отобразился значок «Eсть монетизация». Вы получаете вознаграждение за целевые действия пользователей, совершивших переход по ссылке в вашем посте.',
                            )}
                        </Typography>
                    </AdvertisingCatalogInfoDialog>
                }
            />
            <DialogRoute
                name={DIALOGS.CPA_ADVERTISING_SHOPS_INFO}
                element={
                    <AdvertisingCatalogInfoDialog title={t('Товары с сайта магазина')}>
                        <Typography variant="body">
                            {t('Найдите магазин и выберите товар из него.')}
                        </Typography>
                        <Typography variant="body" pt={1}>
                            {t(
                                'Здесь собраны магазины, которые продвигают свои площадки продаж. Все товары из этих магазинов участвуют в программе монетизации. Перейдите на сайт бренда, выберите товар для продвижения и скопируйте ссылку на него. При создании поста добавьте ссылку на товар в поле «Товары в посте» и убедитесь, что отобразился значок «Eсть монетизация». Вы получаете вознаграждение за целевые действия пользователей, совершивших переход по ссылке в вашем посте.',
                            )}
                        </Typography>
                    </AdvertisingCatalogInfoDialog>
                }
            />
        </>
    );
};
